import { SetsModal } from '@/components/SetsModal';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useWindowResize } from '@/hooks/useWindowResize';
import { useCallback, useMemo, useState } from 'react';
import { FlatList, Platform, View } from 'react-native';
import { NoSets } from '@/views/NoSets';
import { Set } from '@/views/Set';
import {
  SentenceSetFromApi,
  SetType,
  TextSetFromApi,
  WordSetFromApi,
} from '@/stores/store.types';
import { styles } from './styles';
import { IS_WEB } from '@/utils/platform';

type PatientProfileSetsProps = {
  wordsets: WordSetFromApi[];
  sentencesets: SentenceSetFromApi[];
  textsets: TextSetFromApi[];
  refreshPatient: () => void;
};

export const PatientProfileSets = ({
  wordsets,
  sentencesets,
  textsets,
  refreshPatient,
}: PatientProfileSetsProps) => {
  const { selectedID, fetchPatientsWrapper, selectedPatient, patientsSyncing } =
    useDashboardContext();
  const [isSetsModalVisible, setIsSetsModalVisible] = useState(false);
  const selectedPatientFullName = useMemo(
    () =>
      selectedPatient
        ? `${selectedPatient?.firstName} ${selectedPatient?.lastName}`
        : '',
    [selectedPatient],
  );
  const windowWidth = useWindowResize();

  const sets = [
    {
      title: 'Wörtersets',
      data: wordsets,
    },
    {
      title: 'Sätzesets',
      data: sentencesets,
    },
    {
      title: 'Textesets',
      data: textsets,
    },
  ];

  const noSetsAvailable = useMemo(() => {
    const setsLength = [wordsets.length, sentencesets.length, textsets.length];
    const areAllEmpty = setsLength.every((entry) => entry === 0);
    return areAllEmpty;
  }, [wordsets.length, sentencesets.length, textsets.length]);

  const changeSetsModalVisibility = useCallback((value) => {
    if (!value) {
      return setIsSetsModalVisible((prevState) => !prevState);
    }
    setIsSetsModalVisible(value);
  }, []);

  const isNarrowWeb = windowWidth <= 950 && IS_WEB;

  if (noSetsAvailable) {
    return <NoSets />;
  }

  return (
    <>
      <View style={styles.rowWithNegativeMargin}>
        {isNarrowWeb ? (
          <FlatList
            keyExtractor={(item, index) => index}
            data={sets}
            style={styles.full}
            renderItem={({ item, index }) => (
              <View key={item.title} style={{ marginBottom: 32 }}>
                <Set
                  key={item.title + index}
                  patientsSyncing={patientsSyncing}
                  selectedPatientFullName={selectedPatientFullName}
                  fetchPatientsWrapper={fetchPatientsWrapper}
                  patientID={selectedID}
                  type={item.title as SetType}
                  data={item.data}
                  refreshPatient={refreshPatient}
                />
              </View>
            )}
          />
        ) : (
          sets.map((set, index) => (
            <Set
              patientsSyncing={patientsSyncing}
              selectedPatientFullName={selectedPatientFullName}
              fetchPatientsWrapper={fetchPatientsWrapper}
              patientID={selectedID}
              key={set.title + index}
              type={set.title as SetType}
              data={set.data}
              refreshPatient={refreshPatient}
            />
          ))
        )}
      </View>
      <SetsModal
        isSetsModalVisible={isSetsModalVisible}
        changeSetsModalVisibility={changeSetsModalVisibility}
      />
    </>
  );
};
